// apps/patient/src/pages/classes/MyClasses.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, XCircle } from 'lucide-react';
import { patientApiService } from '../../services/api';

const STATUS_BADGES: Record<string, string> = {
  enrolled: 'bg-blue-100 text-blue-700',
  waitlisted: 'bg-yellow-100 text-yellow-700',
  checked_in: 'bg-green-100 text-green-700',
  attended: 'bg-gray-100 text-gray-500',
  no_show: 'bg-orange-100 text-orange-700',
  cancelled: 'bg-red-100 text-red-700',
};

export const MyClasses = () => {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');

  useEffect(() => {
    loadEnrollments();
  }, []);

  const loadEnrollments = async () => {
    setLoading(true);
    try {
      const data = await patientApiService.getMyClassEnrollments();
      setEnrollments(data || []);
    } catch (err) {
      console.error('Failed to load enrollments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (enrollmentId: string) => {
    if (!confirm('Cancel this class enrollment?')) return;
    try {
      await patientApiService.cancelClassEnrollment(enrollmentId);
      await loadEnrollments();
    } catch (err: any) {
      alert(err.message || 'Failed to cancel');
    }
  };

  const today = new Date().toISOString().split('T')[0];

  const upcoming = enrollments.filter(e =>
    ['enrolled', 'waitlisted', 'checked_in'].includes(e.status) &&
    e.class_session?.session_date >= today
  );

  const past = enrollments.filter(e =>
    ['attended', 'no_show', 'cancelled'].includes(e.status) ||
    (e.class_session?.session_date < today)
  );

  const displayList = activeTab === 'upcoming' ? upcoming : past;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">My Classes</h1>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {(['upcoming', 'past'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'upcoming' ? `Upcoming (${upcoming.length})` : `Past (${past.length})`}
          </button>
        ))}
      </div>

      {displayList.length === 0 ? (
        <p className="text-center text-gray-500 py-8">
          {activeTab === 'upcoming' ? 'No upcoming class sessions.' : 'No past class sessions.'}
        </p>
      ) : (
        <div className="space-y-3">
          {displayList.map((enrollment: any) => {
            const session = enrollment.class_session;
            const definition = enrollment.class_definition;

            return (
              <div key={enrollment.id} className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">{definition?.name || 'Class'}</h3>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {session?.session_date
                          ? new Date(session.session_date + 'T00:00').toLocaleDateString('en-US', {
                              weekday: 'short', month: 'short', day: 'numeric'
                            })
                          : '—'
                        }
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {session?.start_time} - {session?.end_time}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_BADGES[enrollment.status] || 'bg-gray-100 text-gray-500'}`}>
                      {enrollment.status?.replace(/_/g, ' ')}
                    </span>
                    {['enrolled', 'waitlisted'].includes(enrollment.status) && activeTab === 'upcoming' && (
                      <button
                        onClick={() => handleCancel(enrollment.id)}
                        className="text-red-500 hover:text-red-700"
                        title="Cancel"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyClasses;
